import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Calendar, Clock, Scissors, User, CreditCard, QrCode, Upload, Check, Copy, Loader2, FileText } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Service, UserProfile } from '../../types';
import { compressImage } from '../../lib/imageUtils';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  service: Service;
  barber: UserProfile;
  date: string;
  time: string;
  totalPrice: number;
  onConfirm: (paymentMethod: 'QRIS' | 'Transfer Bank', paymentProof?: string) => Promise<void>;
}

export const CheckoutModal = ({
  isOpen,
  onClose,
  service,
  barber,
  date,
  time,
  totalPrice,
  onConfirm
}: CheckoutModalProps) => {
  const [paymentProof, setPaymentProof] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [isCopied, setIsCopied] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setIsCopied(label);
    setTimeout(() => setIsCopied(null), 2000);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Mohon pilih file gambar bukti transfer');
      return;
    }

    setIsUploading(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const compressed = await compressImage(reader.result as string, 600, 600, 0.7);
        setPaymentProof(compressed);
      } catch (err) {
        console.error('Failed to compress receipt image', err);
      } finally {
        setIsUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleConfirmOrder = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm('QRIS', paymentProof || undefined);
      onClose();
    } catch (err) {
      console.error('Checkout error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-0 sm:p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
      />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative bg-white w-full sm:max-w-2xl h-full sm:h-auto sm:max-h-[92vh] sm:rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="px-6 sm:px-8 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/20 shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center text-white shrink-0 shadow-lg shadow-emerald-500/20">
              <CreditCard className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="font-black text-slate-900 text-lg tracking-tight">Checkout & Pembayaran</h2>
              <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest">Selesaikan pesanan cukur Anda</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-colors shrink-0">
            <X className="w-6 h-6 text-slate-400" />
          </button>
        </div>

        {/* Scrollable Container */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-6 sm:p-8 space-y-6 pb-24 sm:pb-8">
          
          {/* Summary Box */}
          <div className="bg-slate-50 rounded-[2rem] p-6 border border-slate-100/50">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Ringkasan Pesanan</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Scissors className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Layanan</p>
                  <p className="text-xs font-black text-slate-800">{service.name}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <User className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Kapster</p>
                  <p className="text-xs font-black text-slate-800">{barber.name}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Calendar className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Tanggal</p>
                  <p className="text-xs font-black text-slate-800">{date}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Clock className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Waktu</p>
                  <p className="text-xs font-black text-slate-800">{time}</p>
                </div>
              </div>
            </div>
            
            <div className="mt-5 pt-4 border-t border-slate-200/60 flex items-center justify-between">
              <p className="text-sm font-black text-slate-800 uppercase tracking-tight">Total Pembayaran</p>
              <p className="text-2xl font-black text-emerald-600">Rp {totalPrice.toLocaleString()}</p>
            </div>
          </div>

          {/* Payment Method Selector */}
          <div className="space-y-3">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Metode Pembayaran</label>
            <div className="p-4 bg-emerald-50/50 rounded-2xl border-2 border-emerald-500 font-black text-xs uppercase tracking-wider text-emerald-800 flex items-center justify-center gap-2">
              <QrCode className="w-4 h-4" />
              <span>QRIS (Scan & Bayar Instan)</span>
            </div>
          </div>

          {/* Payment Instructions Details */}
          <div className="border border-slate-100 rounded-[2rem] p-6 bg-white shadow-sm overflow-hidden min-h-[180px]">
            <div className="flex flex-col items-center text-center space-y-4">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Pindai Kode QRIS CukurJe</p>
              
              {/* Styled QRIS frame with User's custom image */}
              <div className="relative w-52 h-52 bg-white rounded-2xl border-2 border-slate-900 p-2 flex items-center justify-center shadow-md overflow-hidden">
                <img 
                  src="https://lh3.googleusercontent.com/d/1po6W-EyC2B-uLz4oeAKFjRTwyeBBToNl" 
                  alt="CukurJe QRIS Kode" 
                  referrerPolicy="no-referrer"
                  className="w-full h-full object-contain rounded-lg"
                  onError={(e) => {
                    // Fallback in case loading fails
                    (e.target as HTMLImageElement).src = "https://drive.google.com/uc?export=view&id=1po6W-EyC2B-uLz4oeAKFjRTwyeBBToNl";
                  }}
                />
              </div>

              <div className="space-y-1">
                <p className="text-xs font-bold text-slate-700">Scan QRIS dari Mobile Banking atau E-Wallet pilihan Anda</p>
                <p className="text-[10px] text-slate-400 font-medium">BCA, Mandiri, Gopay, OVO, DANA, LinkAja, Sakuku, ShopeePay</p>
              </div>
            </div>
          </div>

          {/* Upload Transfer Receipt Area */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Unggah Bukti Transfer / Pembayaran</label>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              className="hidden"
            />
            
            <div 
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                "border-2 border-dashed rounded-[2rem] p-6 text-center cursor-pointer transition-all flex flex-col items-center justify-center min-h-[140px]",
                paymentProof 
                  ? "border-emerald-200 bg-emerald-50/10 hover:bg-emerald-50/20" 
                  : "border-slate-200 hover:border-emerald-500 bg-slate-50/50 hover:bg-emerald-50/5"
              )}
            >
              {isUploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
                  <p className="text-xs font-bold text-slate-600">Sedang memproses gambar...</p>
                </div>
              ) : paymentProof ? (
                <div className="flex flex-col items-center gap-3">
                  <div className="relative">
                    <img 
                      src={paymentProof} 
                      alt="Receipt Preview" 
                      className="w-24 h-24 object-cover rounded-xl shadow-md border-2 border-white ring-4 ring-emerald-50"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPaymentProof('');
                      }}
                      className="absolute -top-1.5 -right-1.5 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all shadow"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="flex items-center gap-1.5 text-emerald-600">
                    <Check className="w-4 h-4" />
                    <span className="text-xs font-black uppercase tracking-wider">Bukti Pembayaran QRIS Dipilih</span>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 text-slate-400">
                  <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center border border-slate-100 shadow-sm text-slate-400">
                    <Upload className="w-5 h-5" />
                  </div>
                  <p className="text-xs font-bold text-slate-600 mt-2">Pilih atau Seret Foto Bukti Pembayaran QRIS</p>
                  <p className="text-[10px] text-slate-400">Format gambar JPEG, PNG (Maks 10MB)</p>
                </div>
              )}
            </div>
          </div>

          {/* Prompt */}
          <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 flex gap-3 text-amber-800">
            <FileText className="w-5 h-5 shrink-0 text-amber-600" />
            <p className="text-[11px] font-bold leading-relaxed">
              Setelah sukses memindai QRIS dan melakukan pembayaran, unggah foto bukti bayar/screenshot transaksi di atas dan klik tombol di bawah untuk mengonfirmasi pesanan. Tim CukurJe akan langsung memproses antrean Anda.
            </p>
          </div>
        </div>

        {/* Footer Area */}
        <div className="absolute bottom-0 inset-x-0 p-6 sm:p-8 bg-white border-t border-slate-100 flex gap-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 py-4 bg-slate-50 hover:bg-slate-100 hover:text-slate-800 text-slate-500 rounded-2xl font-bold text-sm transition-all border border-slate-100 cursor-pointer text-center disabled:opacity-50"
          >
            Batal
          </button>
          <button
            type="button"
            onClick={handleConfirmOrder}
            disabled={isSubmitting || isUploading}
            className="flex-[2] py-4 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-200 disabled:cursor-not-allowed text-white rounded-2xl font-black text-sm tracking-wide transition-all shadow-xl shadow-emerald-500/20 flex items-center justify-center gap-2 cursor-pointer"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Selesai Memproses...
              </>
            ) : (
              'Selesaikan & Bayar'
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};
